import React, { useEffect, useState } from 'react';
import { FieldProps } from '@rjsf/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

interface Destination {
  server: string;
  namespace: string;
  name: string;        // ArgoCD Project Destination 的 name（Project 内部管理用）
  clusterName?: string; // ArgoCD 注册的集群名称（用于 --dest-name）
}

interface EnvironmentDestinationPickerProps extends FieldProps<string> {
  uiSchema?: {
    'ui:options'?: {
      argocdProjectField?: string; // 关联的 ArgoCD Project 字段名
      environmentLabel?: string; // 环境标签（如 TEST, STAGING, PROD）
    };
  };
}

/**
 * EnvironmentDestinationPicker
 *
 * 自定义字段：从选中的 ArgoCD Project 的 destinations 中选择一条作为环境配置
 *
 * 使用方式：
 * ```yaml
 * testEnvironment:
 *   title: TEST 环境
 *   type: string
 *   ui:field: EnvironmentDestinationPicker
 *   ui:options:
 *     argocdProjectField: argocdProject  # 关联的 ArgoCD Project 字段
 *     environmentLabel: TEST
 * ```
 *
 * 返回值格式: JSON '{"server":"...","namespace":"...","name":"...","clusterName":"..."}'
 * 向后兼容: 可解析旧格式 "clusterName|server|namespace"
 */
export const EnvironmentDestinationPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  idSchema,
  schema,
  uiSchema,
}: EnvironmentDestinationPickerProps) => {
  const catalogApi = useApi(catalogApiRef);
  const options = uiSchema?.['ui:options'] || {};
  const argocdProjectField = options.argocdProjectField || 'argocdProject';
  const environmentLabel = options.environmentLabel || '';

  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  // 获取关联的 ArgoCD Project 值
  const argocdProjectRef = formContext?.formData?.[argocdProjectField];

  useEffect(() => {
    const loadDestinations = async () => {
      if (!argocdProjectRef) {
        setDestinations([]);
        return;
      }

      try {
        setLoading(true);
        setError(undefined);

        // 从 Catalog 获取 ArgoCD Project 实体
        // argocdProjectRef 格式: resource:default/argocd-xxx
        const [kind, rest] = argocdProjectRef.split(':');
        const [namespace, name] = rest.split('/');

        const entity = await catalogApi.getEntityByRef({
          kind,
          namespace,
          name,
        });

        if (!entity) {
          setError('找不到 ArgoCD Project');
          setDestinations([]);
          return;
        }

        // 从 annotation 中解析 destinations
        const destinationsJson =
          entity.metadata.annotations?.['hose.io/destinations'] || '[]';
        const parsedDestinations: Destination[] = JSON.parse(destinationsJson);

        // 过滤掉通配符配置
        const validDestinations = parsedDestinations.filter(
          d => d.server !== '*' && d.namespace !== '*'
        );

        setDestinations(validDestinations);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load destinations'
        );
        setDestinations([]);
      } finally {
        setLoading(false);
      }
    };

    loadDestinations();
  }, [catalogApi, argocdProjectRef]);

  // 解析当前选中的值（支持 JSON 格式，向后兼容旧格式）
  const parseValue = (value: string | undefined) => {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      return {
        clusterName: parsed.clusterName || parsed.name || '',
        server: parsed.server || '',
        namespace: parsed.namespace || '',
        name: parsed.name || '',
      };
    } catch {
      // 向后兼容旧格式 "clusterName|server|namespace"
      const [clusterName, server, namespace] = value.split('|');
      return { clusterName, server, namespace, name: clusterName };
    }
  };

  const selectedValue = parseValue(formData);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title || `${environmentLabel} 环境`}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading || !argocdProjectRef}
      >
        {!argocdProjectRef && (
          <MenuItem value="" disabled>
            请先选择 ArgoCD Project
          </MenuItem>
        )}
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && destinations.length === 0 && argocdProjectRef && (
          <MenuItem value="" disabled>
            无可用 Destination（请检查 ArgoCD Project 配置）
          </MenuItem>
        )}
        {destinations.map((dest, index) => {
          // 输出 JSON 格式，与 ArgoCD Project Resource 的 hose.io/destinations 对齐
          const value = JSON.stringify({
            server: dest.server,
            namespace: dest.namespace,
            name: dest.name || '',              // Project Destination 的 name
            clusterName: dest.clusterName || '', // ArgoCD 注册的集群名称，用于 --dest-name
          });
          return (
            <MenuItem key={index} value={value}>
              <Box>
                <Typography variant="body1">
                  <strong>{dest.name || '未命名集群'}</strong> / {dest.namespace}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {dest.server}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Select>
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>请选择 {environmentLabel} 环境</FormHelperText>
      )}
      {selectedValue && (
        <FormHelperText>
          集群: {selectedValue.clusterName} | 命名空间: {selectedValue.namespace}
        </FormHelperText>
      )}
    </FormControl>
  );
};

